import {
    parsePhoneNumberFromString,
    AsYouType,
    getCountries,
    getCountryCallingCode,
    getExampleNumber,
} from "libphonenumber-js";
import phoneExamples from "libphonenumber-js/examples.mobile.json";

const DEFAULT_COUNTRY = "BR";

/**
 * Format a phone number for display in its national format.
 * Accepts canonical E.164 (e.g. "+5511999998888") or a national string.
 * Falls back to the original value when it cannot be parsed.
 */
export function formatPhoneNational(value, defaultCountry = DEFAULT_COUNTRY) {
    if (!value) {
        return "";
    }
    const raw = String(value).trim();
    try {
        const phone = parsePhoneNumberFromString(
            raw,
            raw.startsWith("+") ? undefined : defaultCountry
        );
        if (phone) {
            const national = phone.formatNational();
            // Omit the country code when the number belongs to the place's
            // country; otherwise prefix the calling code (e.g. "+55 (48) ...").
            if (phone.country && phone.country === defaultCountry) {
                return national;
            }
            return `+${phone.countryCallingCode} ${national}`;
        }
    } catch (e) {
        // ignore and fall through
    }
    return raw;
}

/**
 * Convert any input to canonical E.164 (e.g. "+5511999998888") or null.
 */
export function toE164(value, defaultCountry = DEFAULT_COUNTRY) {
    if (!value) {
        return null;
    }
    const raw = String(value).trim();
    try {
        const phone = parsePhoneNumberFromString(
            raw,
            raw.startsWith("+") ? undefined : defaultCountry
        );
        if (phone) {
            return phone.number;
        }
    } catch (e) {
        // ignore
    }
    return null;
}

/**
 * Validate a phone number for the given default country (or its own DDI).
 */
export function isValidPhone(value, defaultCountry = DEFAULT_COUNTRY) {
    if (!value) {
        return false;
    }
    const raw = String(value).trim();
    try {
        const phone = parsePhoneNumberFromString(
            raw,
            raw.startsWith("+") ? undefined : defaultCountry
        );
        return !!phone && phone.isValid();
    } catch (e) {
        return false;
    }
}

/**
 * Digits-only representation including country code, suitable for building
 * wa.me / api.whatsapp.com links. Returns digits without the leading "+".
 */
export function phoneDigits(value, defaultCountry = DEFAULT_COUNTRY) {
    const e164 = toE164(value, defaultCountry);
    if (e164) {
        return e164.replace(/[^0-9]/g, "");
    }
    return String(value || "").replace(/[^0-9]/g, "");
}

/**
 * Calling code (e.g. "55") for an ISO country code.
 */
export function callingCodeFor(iso = DEFAULT_COUNTRY) {
    try {
        return getCountryCallingCode(iso);
    } catch (e) {
        return "55";
    }
}

/**
 * Example national-format number for a country, suitable as an input
 * placeholder/mask hint (e.g. "(11) 91234-5678" for BR).
 */
export function exampleNationalFor(iso = DEFAULT_COUNTRY) {
    try {
        const example = getExampleNumber(iso, phoneExamples);
        if (example) {
            return example.formatNational();
        }
    } catch (e) {
        // ignore
    }
    return "";
}

let regionNames = null;
function getRegionNames() {
    if (!regionNames && typeof Intl !== "undefined" && Intl.DisplayNames) {
        regionNames = new Intl.DisplayNames(["pt-BR"], { type: "region" });
    }
    return regionNames;
}

function flagFor(iso) {
    return iso
        .toUpperCase()
        .replace(/./g, (char) =>
            String.fromCodePoint(char.charCodeAt(0) + 127397)
        );
}

/**
 * Country options for a country-aware phone input.
 * Returns: [{ iso, dialCode, name, flag, label }]
 */
export function countryOptions() {
    const names = getRegionNames();
    return getCountries()
        .map((iso) => {
            const dialCode = getCountryCallingCode(iso);
            const name = (names && names.of(iso)) || iso;
            const flag = flagFor(iso);
            return {
                iso,
                dialCode,
                name,
                flag,
                label: `${flag} ${name} (+${dialCode})`,
            };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Parse a full (E.164 or DDI-prefixed) number into its national display part
 * and detected ISO country. Returns { national, isoCode }.
 */
export function parseFullNumber(raw, fallbackIso = DEFAULT_COUNTRY) {
    const formatter = new AsYouType();
    formatter.input(String(raw || ""));
    const phone = formatter.getNumber();
    if (phone) {
        const iso = phone.country || fallbackIso;
        const { national } = processTypedNumber(iso, phone.nationalNumber || "");
        return { national, isoCode: iso };
    }
    const digits = String(raw || "").replace(/\D/g, "");
    const { national } = processTypedNumber(fallbackIso, digits);
    return { national, isoCode: fallbackIso };
}

/**
 * Maximum number of national-significant digits a country accepts, derived
 * from its example number. Returns 0 when it cannot be determined (no cap).
 */
export function nationalDigitLimit(iso = DEFAULT_COUNTRY) {
    try {
        const example = getExampleNumber(iso, phoneExamples);
        if (example && example.nationalNumber) {
            return String(example.nationalNumber).length;
        }
    } catch (e) {
        // ignore
    }
    return 0;
}

/**
 * Format the national part of a number as the user types, given a country.
 * Uses the country's national formatting (e.g. "(48) 99959-4159") and caps
 * the digits to what the country accepts. Returns { national, e164, isValid, isoCode }.
 */
export function processTypedNumber(iso, digitsOnly) {
    const limit = nationalDigitLimit(iso);
    let digits = String(digitsOnly || "").replace(/\D/g, "");
    if (limit && digits.length > limit) {
        digits = digits.slice(0, limit);
    }

    const formatter = new AsYouType(iso);
    const national = formatter.input(digits);
    const phone = formatter.getNumber();

    return {
        national,
        digits,
        e164: phone ? phone.number : `+${callingCodeFor(iso)}${digits}`,
        isValid: phone ? phone.isValid() : false,
        isoCode: phone ? phone.country : iso,
    };
}
